import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const t = (key) => {
    const texts = {
      title: { fr: 'Mot de passe oublié', en: 'Forgot password', es: 'Contraseña olvidada', de: 'Passwort vergessen', pt: 'Esqueci a senha' },
      subtitle: { fr: 'Entrez votre adresse e-mail et nous vous enverrons un lien pour réinitialiser votre mot de passe.', en: 'Enter your email address and we\'ll send you a link to reset your password.', es: 'Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.', de: 'Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.', pt: 'Digite seu e-mail e enviaremos um link para redefinir sua senha.' },
      send: { fr: 'Envoyer le lien', en: 'Send link', es: 'Enviar enlace', de: 'Link senden', pt: 'Enviar link' },
      sent_title: { fr: 'E-mail envoyé !', en: 'Email sent!', es: '¡Correo enviado!', de: 'E-Mail gesendet!', pt: 'E-mail enviado!' },
      sent_text: { fr: 'Si un compte existe avec cette adresse, vous recevrez un lien de réinitialisation.', en: 'If an account exists with this address, you will receive a reset link.', es: 'Si existe una cuenta con esta dirección, recibirás un enlace de restablecimiento.', de: 'Wenn ein Konto mit dieser Adresse existiert, erhalten Sie einen Link zum Zurücksetzen.', pt: 'Se uma conta existir com este endereço, você receberá um link de redefinição.' },
      back_login: { fr: 'Retour à la connexion', en: 'Back to login', es: 'Volver al inicio de sesión', de: 'Zurück zur Anmeldung', pt: 'Voltar ao login' },
    };
    return texts[key]?.[language] || texts[key]?.en || key;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (error) {
      toast.error(language === 'fr' ? 'Une erreur est survenue' : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-velvet-bg flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-velvet-bg via-velvet-bg to-purple-dark/20" />
      <div className="absolute top-1/4 -left-32 w-64 h-64 bg-purple-main/20 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 -right-32 w-64 h-64 bg-fuchsia-main/10 rounded-full blur-3xl" />
      
      <div className="relative z-10 w-full max-w-md">
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 text-velvet_text-secondary hover:text-velvet_text-accent mb-8 transition-colors"
          data-testid="back-to-login-link"
        >
          <ArrowLeft size={20} />
          {t('back_login')}
        </Link>
        
        <div className="glass rounded-2xl p-8 animate-slideUp">
          {sent ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle size={32} className="text-green-400" />
              </div>
              <h2 className="font-heading text-2xl text-velvet_text-primary mb-3">{t('sent_title')}</h2>
              <p className="text-velvet_text-secondary mb-6">{t('sent_text')}</p>
              <Link to="/login">
                <Button className="w-full bg-purple-main hover:bg-purple-dark text-white rounded-full h-12" data-testid="back-to-login-button">
                  {t('back_login')}
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-main to-fuchsia-main flex items-center justify-center">
                <Mail size={28} className="text-white" />
              </div>
              <h1 className="font-heading text-3xl text-velvet_text-primary mb-2 text-center">{t('title')}</h1>
              <p className="text-velvet_text-secondary mb-8 text-center text-sm">{t('subtitle')}</p>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-velvet_text-secondary">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-velvet-paper border-white/10 text-velvet_text-primary h-12"
                    placeholder="votre@email.com"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-purple-main hover:bg-purple-dark text-white rounded-full h-12 font-semibold shadow-neon"
                  data-testid="forgot-submit-button"
                >
                  {loading ? <Loader2 className="animate-spin" size={20} /> : t('send')}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
